import { useEffect, useRef, useState, useCallback } from 'react';
import { socketService } from '../lib/socketService';
import { sendToTerminal } from '../lib/terminalLogger';
import { Campanha } from '../types/quiz';
import { useWebSocketConnection } from '../contexts/WebSocketContext';

const LOG_PREFIX = '[CAMPANHAS_WEBSOCKET]';

interface UseCampanhasWebSocketOptions {
  onNewCampanha?: (campanha: Campanha) => void;
  onCampanhaActivated?: (campanha: Campanha) => void;
  onCampanhaEndingSoon?: (campanha: Campanha) => void;
  /** Se true, não vai logar mensagens no terminal */
  silent?: boolean;
}

/**
 * Hook para gerenciar eventos WebSocket de campanhas
 * 
 * Otimizado para performance:
 * - Usa refs para handlers evitando re-renders
 * - Limpa listeners corretamente no cleanup
 * - Detecta conexão via eventos do socket ao invés de polling
 */
export function useCampanhasWebSocket(
  userId: number | null | undefined,
  options: UseCampanhasWebSocketOptions = {}
) {
  const { isEnabled } = useWebSocketConnection();
  const [isConnected, setIsConnected] = useState(false);
  const handlersRef = useRef<UseCampanhasWebSocketOptions>(options);
  const listenersRegisteredRef = useRef(false);
  const noUserIdLoggedRef = useRef(false); // Evita log duplicado "sem userId"

  // Atualizar handlers quando mudarem (sem causar re-render do effect)
  useEffect(() => {
    handlersRef.current = options;
  }, [options]);

  // Configurar listeners WebSocket
  useEffect(() => {
    // Se não tiver userId, não configurar (log apenas uma vez)
    if (!userId) {
      if (!noUserIdLoggedRef.current && !handlersRef.current.silent) {
        sendToTerminal(`⏸️ ${LOG_PREFIX} WebSocket aguardando userId`);
        noUserIdLoggedRef.current = true;
      }
      return;
    }

    if (!isEnabled) {
      if (!handlersRef.current.silent) {
        sendToTerminal(`⏸️ ${LOG_PREFIX} WebSocket aguardando habilitação central`, {
          userId
        });
      }
      return;
    }
    // Reset do flag quando userId fica disponível
    noUserIdLoggedRef.current = false;

    const socket = socketService.getSocket({
      source: 'hook-campanhas',
      reason: 'register-campaign-listeners',
    });
    if (!socket) {
      setIsConnected(false);
      if (!handlersRef.current.silent) {
        sendToTerminal(`⚠️ ${LOG_PREFIX} WebSocket indisponível por configuração`, {
          userId,
          error: socketService.getConfigurationError(),
        });
      }
      return;
    }
    const silent = handlersRef.current.silent;

    // Handler para nova campanha
    const handleNewCampanha = (data: { campanha: Campanha }) => {
      if (!silent) {
        sendToTerminal(`📨 ${LOG_PREFIX} Evento: campanha:new`, {
          campanhaId: data.campanha?.id,
          titulo: data.campanha?.titulo
        });
      }

      if (handlersRef.current.onNewCampanha && data.campanha) {
        handlersRef.current.onNewCampanha(data.campanha);
      }
    };

    // Handler para campanha ativada
    const handleCampanhaActivated = (data: { campanha: Campanha }) => {
      if (!silent) {
        sendToTerminal(`✅ ${LOG_PREFIX} Evento: campanha:activated`, {
          campanhaId: data.campanha?.id,
          titulo: data.campanha?.titulo
        });
      }

      if (handlersRef.current.onCampanhaActivated && data.campanha) {
        handlersRef.current.onCampanhaActivated(data.campanha);
      }
    };

    // Handler para campanha terminando em breve
    const handleCampanhaEndingSoon = (data: { campanha: Campanha; diasRestantes: number }) => {
      if (!silent) {
        sendToTerminal(`⏰ ${LOG_PREFIX} Evento: campanha:ending_soon`, {
          campanhaId: data.campanha?.id,
          titulo: data.campanha?.titulo,
          diasRestantes: data.diasRestantes
        });
      }

      if (handlersRef.current.onCampanhaEndingSoon && data.campanha) {
        handlersRef.current.onCampanhaEndingSoon(data.campanha);
      }
    };

    // Handlers de conexão
    const handleConnect = () => {
      setIsConnected(true);
      if (!silent) {
        sendToTerminal(`✅ ${LOG_PREFIX} WebSocket conectado`, { userId });
      }
    };

    const handleDisconnect = () => {
      setIsConnected(false);
      if (!silent) {
        sendToTerminal(`⚠️ ${LOG_PREFIX} WebSocket desconectado`, { userId });
      }
    };

    // Verificar estado inicial da conexão
    const initialConnected = socketService.isConnected();
    setIsConnected(initialConnected);

    // Registrar todos os listeners
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('campanha:new', handleNewCampanha);
    socket.on('campanha:activated', handleCampanhaActivated);
    socket.on('campanha:ending_soon', handleCampanhaEndingSoon);
    listenersRegisteredRef.current = true;

    if (!silent) {
      sendToTerminal(`👂 ${LOG_PREFIX} Listeners registrados`, {
        userId,
        connected: initialConnected,
        events: ['campanha:new', 'campanha:activated', 'campanha:ending_soon']
      });
    }

    // Cleanup - remover todos os listeners
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('campanha:new', handleNewCampanha);
      socket.off('campanha:activated', handleCampanhaActivated);
      socket.off('campanha:ending_soon', handleCampanhaEndingSoon);
      listenersRegisteredRef.current = false;
      
      if (!silent) {
        sendToTerminal(`🧹 ${LOG_PREFIX} Listeners removidos`);
      }
    };
  }, [userId, isEnabled]); // userId + habilitação central

  return {
    isConnected,
    /** @deprecated Use isConnected ao invés */
    useWebSocket: isConnected
  };
}
